import { useEffect, useRef } from 'react';
import { Box, useMantineTheme } from '@mantine/core';

interface Props {
  analyser: AnalyserNode | null;
  height?: number;
}

const BAR_WIDTH = 3;
const BAR_GAP = 2;
const MIN_BAR_HEIGHT = 2;
// Advance the scroll one bar every N animation frames. 60fps / 5 ≈ 12 bars/sec.
const FRAMES_PER_BAR = 5;

/**
 * ChatGPT-style amplitude bar visualizer. Maintains a scrolling history of
 * peak amplitudes sampled at ~60fps from an AnalyserNode and draws them as
 * centered vertical bars.
 */
export const Waveform = ({ analyser, height = 40 }: Props) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const historyRef = useRef<number[]>([]);
  const rafRef = useRef<number>(undefined);
  const theme = useMantineTheme();

  useEffect(() => {
    if (!analyser) return;

    const canvas = canvasRef.current;
    const container = containerRef.current;
    if (!canvas || !container) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const dpr = window.devicePixelRatio || 1;
    const resize = () => {
      const w = container.clientWidth;
      const h = height;
      canvas.width = w * dpr;
      canvas.height = h * dpr;
      canvas.style.width = `${w}px`;
      canvas.style.height = `${h}px`;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };
    resize();

    const resizeObserver = new ResizeObserver(resize);
    resizeObserver.observe(container);

    const timeData = new Float32Array(analyser.fftSize);
    const color = theme.colors.teal[6];
    let frameCounter = 0;
    let rollingPeak = 0;

    const tick = () => {
      analyser.getFloatTimeDomainData(timeData);

      let peak = 0;
      for (let i = 0; i < timeData.length; i++) {
        const v = Math.abs(timeData[i]);
        if (v > peak) peak = v;
      }
      if (peak > rollingPeak) rollingPeak = peak;

      const w = canvas.clientWidth;
      const h = canvas.clientHeight;
      const maxBars = Math.floor(w / (BAR_WIDTH + BAR_GAP));

      frameCounter++;
      if (frameCounter >= FRAMES_PER_BAR) {
        historyRef.current.push(rollingPeak);
        rollingPeak = 0;
        frameCounter = 0;
        if (historyRef.current.length > maxBars) {
          historyRef.current.splice(0, historyRef.current.length - maxBars);
        }
      }

      ctx.clearRect(0, 0, w, h);
      ctx.fillStyle = color;

      const history = historyRef.current;
      const startX = w - history.length * (BAR_WIDTH + BAR_GAP);
      const centerY = h / 2;

      for (let i = 0; i < history.length; i++) {
        const amp = Math.min(1, history[i] * 2.5);
        const barH = Math.max(MIN_BAR_HEIGHT, amp * h);
        const x = startX + i * (BAR_WIDTH + BAR_GAP);
        ctx.fillRect(x, centerY - barH / 2, BAR_WIDTH, barH);
      }

      rafRef.current = requestAnimationFrame(tick);
    };

    rafRef.current = requestAnimationFrame(tick);

    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      resizeObserver.disconnect();
      historyRef.current = [];
    };
  }, [analyser, height, theme]);

  return (
    <Box ref={containerRef} sx={{ flex: 1, height, minWidth: 0 }}>
      <canvas ref={canvasRef} />
    </Box>
  );
};
